import { useEffect, useRef } from "react";

export default function FestivalBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animFrame;
    let W, H;

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // --- Spotlights ---
    const spotlights = Array.from({ length: 6 }, (_, i) => ({
      x: (W / 6) * i + W / 12,
      baseX: (W / 6) * i + W / 12,
      angle: Math.random() * Math.PI * 2,
      speed: (Math.random() - 0.5) * 0.012,
      color: [
        "rgba(255,80,180,",
        "rgba(80,180,255,",
        "rgba(255,220,80,",
        "rgba(120,255,120,",
        "rgba(255,120,80,",
        "rgba(180,80,255,",
      ][i % 6],
      swing: 60 + Math.random() * 80,
    }));

    // --- Smoke particles ---
    const smokes = Array.from({ length: 35 }, () => ({
      x: Math.random() * W,
      y: H * 0.55 + Math.random() * H * 0.45,
      r: 20 + Math.random() * 60,
      vx: (Math.random() - 0.5) * 0.4,
      vy: -(0.3 + Math.random() * 0.5),
      alpha: 0.04 + Math.random() * 0.08,
      grow: 0.15 + Math.random() * 0.25,
    }));

    // --- Crowd silhouettes ---
    const crowdHeads = Array.from({ length: 55 }, (_, i) => ({
      x: (W / 55) * i + Math.random() * (W / 55),
      y: H * 0.72 + Math.sin(i * 1.3) * 18,
      phase: Math.random() * Math.PI * 2,
      speed: 0.015 + Math.random() * 0.025,
      h: 28 + Math.random() * 22,
      arm: Math.random() > 0.6,
    }));

    // --- Laser beams ---
    const lasers = Array.from({ length: 8 }, (_, i) => ({
      x: (W / 8) * i + W / 16,
      angle: (Math.random() - 0.5) * 0.8,
      speed: (Math.random() - 0.5) * 0.008,
      color: ["#ff50b4", "#50b4ff", "#ffe050", "#50ff78", "#ff7850"][i % 5],
    }));

    // --- Floating particles (glitter) ---
    const glitter = Array.from({ length: 80 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H * 0.8,
      vx: (Math.random() - 0.5) * 0.6,
      vy: -(0.2 + Math.random() * 0.5),
      r: 1 + Math.random() * 2.5,
      alpha: Math.random(),
      fade: 0.005 + Math.random() * 0.01,
      color: ["#fff", "#ffec80", "#ff80ec", "#80ecff"][Math.floor(Math.random() * 4)],
    }));

    let tick = 0;

    function drawBackground() {
      // Deep dark gradient
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, "#0a0010");
      bg.addColorStop(0.6, "#100018");
      bg.addColorStop(1, "#1a0010");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
    }

    function drawStage() {
      // Stage floor glow
      const stageY = H * 0.7;
      const glow = ctx.createLinearGradient(0, stageY - 40, 0, stageY + 60);
      glow.addColorStop(0, "rgba(200,80,255,0.0)");
      glow.addColorStop(0.4, "rgba(200,80,255,0.12)");
      glow.addColorStop(1, "rgba(80,40,120,0.4)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, stageY - 40, W, 100);

      // Stage floor line
      ctx.strokeStyle = "rgba(200,80,255,0.35)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, stageY);
      ctx.lineTo(W, stageY);
      ctx.stroke();

      // Pixel screen (center top)
      const sw = Math.min(W * 0.45, 420);
      const sh = sw * 0.45;
      const sx = W / 2 - sw / 2;
      const sy = H * 0.06;

      // Pixelated noise effect on screen
      ctx.save();
      const blockSize = 8;
      for (let bx = 0; bx < sw; bx += blockSize) {
        for (let by = 0; by < sh; by += blockSize) {
          const noise = Math.random();
          const hue = (tick * 0.5 + bx + by * 0.3) % 360;
          ctx.fillStyle = `hsla(${hue}, 80%, ${30 + noise * 40}%, ${0.6 + noise * 0.4})`;
          ctx.fillRect(sx + bx, sy + by, blockSize - 1, blockSize - 1);
        }
      }
      // Screen border
      ctx.strokeStyle = "rgba(255,255,255,0.25)";
      ctx.lineWidth = 3;
      ctx.strokeRect(sx, sy, sw, sh);
      // Screen glow
      const screenGlow = ctx.createRadialGradient(W / 2, sy + sh / 2, 10, W / 2, sy + sh / 2, sw * 0.6);
      screenGlow.addColorStop(0, `hsla(${(tick * 0.5) % 360}, 80%, 60%, 0.15)`);
      screenGlow.addColorStop(1, "transparent");
      ctx.fillStyle = screenGlow;
      ctx.fillRect(sx - 60, sy - 40, sw + 120, sh + 80);
      ctx.restore();

      // Speaker stacks
      const spkW = 30, spkH = 90;
      [[sx - spkW - 10, stageY - spkH], [sx + sw + 10, stageY - spkH]].forEach(([x, y]) => {
        ctx.fillStyle = "#1a1a2e";
        ctx.fillRect(x, y, spkW, spkH);
        ctx.strokeStyle = "rgba(255,255,255,0.1)";
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, spkW, spkH);
        for (let k = 0; k < 4; k++) {
          ctx.beginPath();
          ctx.arc(x + spkW / 2, y + 12 + k * 20, 8, 0, Math.PI * 2);
          ctx.fillStyle = "#111";
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.15)";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      });
    }

    function drawSpotlights() {
      spotlights.forEach(s => {
        s.angle += s.speed;
        s.x = s.baseX + Math.sin(s.angle) * s.swing;

        const stageY = H * 0.7;
        ctx.save();
        const grad = ctx.createLinearGradient(s.x, 0, s.x + (s.x - W / 2) * 0.1, stageY);
        grad.addColorStop(0, s.color + "0.0)");
        grad.addColorStop(0.3, s.color + "0.12)");
        grad.addColorStop(0.9, s.color + "0.22)");
        grad.addColorStop(1, s.color + "0.0)");

        ctx.beginPath();
        ctx.moveTo(s.x, 0);
        ctx.lineTo(s.x - 25, stageY);
        ctx.lineTo(s.x + 25, stageY);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();

        // spotlight base dot
        ctx.beginPath();
        ctx.arc(s.x, 8, 5, 0, Math.PI * 2);
        ctx.fillStyle = s.color + "0.9)";
        ctx.fill();
        ctx.restore();
      });
    }

    function drawLasers() {
      lasers.forEach(l => {
        l.angle += l.speed;
        if (Math.abs(l.angle) > 0.9) l.speed *= -1;

        ctx.save();
        ctx.globalAlpha = 0.55;
        ctx.strokeStyle = l.color;
        ctx.lineWidth = 1.5;
        ctx.shadowColor = l.color;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(l.x, H * 0.68);
        ctx.lineTo(l.x + Math.sin(l.angle) * H * 0.9, H * 0.68 - H * 0.65);
        ctx.stroke();
        ctx.restore();
      });
    }

    function drawSmoke() {
      smokes.forEach(s => {
        s.x += s.vx;
        s.y += s.vy;
        s.r += s.grow;
        if (s.y < H * 0.2 || s.r > 160) {
          s.x = Math.random() * W;
          s.y = H * 0.72 + Math.random() * H * 0.1;
          s.r = 20 + Math.random() * 30;
        }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180,140,220,${s.alpha})`;
        ctx.fill();
      });
    }

    function drawCrowd() {
      const stageY = H * 0.7;
      crowdHeads.forEach(c => {
        c.phase += c.speed;
        const bob = Math.sin(c.phase) * 5;
        const headY = stageY + bob;

        // Body
        ctx.fillStyle = "rgba(20,10,30,0.9)";
        ctx.beginPath();
        ctx.roundRect(c.x - 8, headY, 16, c.h, 3);
        ctx.fill();

        // Head
        ctx.beginPath();
        ctx.arc(c.x, headY - 6, 9, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(30,15,40,0.95)";
        ctx.fill();

        // Raised arm (some people)
        if (c.arm) {
          const armAngle = Math.sin(c.phase * 1.5) * 0.4 - 1.2;
          ctx.strokeStyle = "rgba(20,10,30,0.9)";
          ctx.lineWidth = 5;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(c.x, headY + 4);
          ctx.lineTo(c.x + Math.cos(armAngle) * 20, headY + 4 + Math.sin(armAngle) * 20);
          ctx.stroke();
        }

        // Phone light (random)
        if (Math.sin(c.phase * 0.3 + c.x) > 0.6) {
          ctx.beginPath();
          ctx.arc(c.x + 10, headY - 14, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,220,0.9)";
          ctx.fill();
          // glow
          ctx.beginPath();
          ctx.arc(c.x + 10, headY - 14, 6, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,200,0.1)";
          ctx.fill();
        }
      });

      // Crowd gradient fade at bottom
      const fade = ctx.createLinearGradient(0, stageY + 20, 0, H);
      fade.addColorStop(0, "transparent");
      fade.addColorStop(1, "rgba(10,0,16,0.95)");
      ctx.fillStyle = fade;
      ctx.fillRect(0, stageY + 20, W, H - stageY - 20);
    }

    function drawGlitter() {
      glitter.forEach(g => {
        g.x += g.vx;
        g.y += g.vy;
        g.alpha -= g.fade;
        if (g.alpha <= 0 || g.y < 0) {
          g.x = Math.random() * W;
          g.y = H * 0.75 + Math.random() * H * 0.2;
          g.alpha = 0.5 + Math.random() * 0.5;
        }
        ctx.save();
        ctx.globalAlpha = g.alpha;
        ctx.fillStyle = g.color;
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
    }

    function loop() {
      tick++;
      ctx.clearRect(0, 0, W, H);
      drawBackground();
      drawSpotlights();
      drawLasers();
      drawSmoke();
      drawStage();
      drawCrowd();
      drawGlitter();
      animFrame = requestAnimationFrame(loop);
    }

    loop();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}